import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useReducedMotion } from "../../style/motion";

const COLORS = [
  "var(--gold)",
  "var(--cherry)",
  "var(--teal)",
  "var(--night)",
];

const Layer = styled.div`
  position: fixed;
  inset: 0;
  z-index: 50;
  overflow: hidden;
  pointer-events: none;
`;

const Bit = styled.span`
  position: absolute;
  top: 0;
  width: 10px;
  height: 14px;
  border-radius: 3px;
  border: 1.5px solid var(--ink);
  animation-name: confetti-fall;
  animation-timing-function: cubic-bezier(0.25, 0.6, 0.4, 1);
  animation-fill-mode: forwards;
`;

type ConfettiProps = { count?: number; duration?: number };

/** One burst of crayon paper bits; unmounts itself. Nothing under reduced motion. */
const Confetti: React.FC<ConfettiProps> = ({ count = 80, duration = 1600 }) => {
  const reduced = useReducedMotion();
  const [done, setDone] = useState(false);

  const bits = useMemo(
    () =>
      Array.from({ length: count }, (_, i) => ({
        left: `${Math.random() * 100}%`,
        color: COLORS[i % COLORS.length],
        delay: Math.random() * 300,
        length: duration * (0.7 + Math.random() * 0.6),
        drift: `${(Math.random() - 0.5) * 160}px`,
        spin: `${(Math.random() > 0.5 ? 1 : -1) * (360 + Math.random() * 540)}deg`,
        round: Math.random() > 0.7,
      })),
    [count, duration],
  );

  useEffect(() => {
    const timeout = setTimeout(() => setDone(true), duration * 1.4 + 300);
    return () => clearTimeout(timeout);
  }, [duration]);

  if (reduced || done) return null;

  return (
    <Layer aria-hidden>
      {bits.map((bit, i) => (
        <Bit
          key={i}
          style={
            {
              left: bit.left,
              background: bit.color,
              borderRadius: bit.round ? "50%" : undefined,
              animationDuration: `${bit.length}ms`,
              animationDelay: `${bit.delay}ms`,
              "--drift": bit.drift,
              "--spin": bit.spin,
            } as React.CSSProperties
          }
        />
      ))}
    </Layer>
  );
};

export default Confetti;
